import { useCallback, useEffect, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { automationService } from "../services/automationService";
import type { AutomationContext, AutomationId, AutomationItem } from "../types";

interface UseAutomationServiceResult {
  automations: AutomationItem[];
  isLoading: boolean;
  search: (params: { term?: string }) => Promise<void>;
  refresh: () => Promise<void>;
  disableAutomation: (params: { automationId: AutomationId }) => Promise<void>;
}

export function useAutomationService(
  context: AutomationContext,
): UseAutomationServiceResult {
  const [automations, setAutomations] = useState<AutomationItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const stableContext = useMemo(
    () => ({
      organizationId: context.organizationId,
      practitionerId: context.practitionerId,
      whatsappBusinessAccountId: context.whatsappBusinessAccountId,
    }),
    [
      context.organizationId,
      context.practitionerId,
      context.whatsappBusinessAccountId,
    ],
  );

  const { run: runList, loading: isLoading } = useAsyncTask<
    [string?],
    AutomationItem[]
  >({
    task: useCallback(
      async (term = "") => {
        const items = await automationService.listByContext({
          context: stableContext,
          filters: { search: term },
        });
        setAutomations(items);
        return items;
      },
      [stableContext],
    ),
  });

  const refresh = useCallback(async () => {
    await runList(searchTerm);
  }, [runList, searchTerm]);

  const search = useCallback(
    async ({ term = "" }: { term?: string }) => {
      setSearchTerm(term);
      await runList(term);
    },
    [runList],
  );

  const disableAutomation = useCallback(
    async ({ automationId }: { automationId: AutomationId }) => {
      await automationService.removeConfigByAutomationId({
        context: stableContext,
        automationId,
      });
      await runList(searchTerm);
    },
    [runList, searchTerm, stableContext],
  );

  useEffect(() => {
    runList("");
  }, [runList]);

  return {
    automations,
    isLoading,
    search,
    refresh,
    disableAutomation,
  };
}
